import { AuthProvider } from '@ankr.com/auth';
import { createSelector } from '@reduxjs/toolkit';
import { getWalletIcon, getWalletName } from '@ankr.com/provider';
import { ChainID } from '@ankr.com/chains-list';

import { RootState } from 'store';
import { getChainIcon } from 'uiKit/utils/getTokenIcon';

import { selectIsLightTheme } from '../../layout/store/themeSlice';
import { getAuthAddress } from '../utils/getAuthAddress';
import { getAuthProvider } from '../utils/getAuthProvider';
import { getAuthReferralCode } from '../utils/getAuthReferralCode';
import { getAuthToken } from '../utils/getAuthToken';
import { getAuthTokenExpiresAt } from '../utils/getAuthTokenExpiresAt';
import { getAuthWalletId } from '../utils/getAuthWalletId';
import { getIsMarketingAccepted } from '../utils/getIsMarketingAccepted';

export const selectAuthState = (state: RootState) => state.auth;

// An address a user logged in with using web3 wallet
export const selectAuthAddress = createSelector(
  selectAuthState,
  state => state.authAddress ?? getAuthAddress(),
);

export const selectAuthProvider = createSelector(
  selectAuthState,
  state => state.authProvider ?? getAuthProvider(),
);

export const selectAuthToken = createSelector(
  selectAuthState,
  state => state.authToken ?? getAuthToken(),
);

export const selectAuthTokenExpiresAt = createSelector(
  selectAuthState,
  state => state.authTokenExpiresAt ?? getAuthTokenExpiresAt(),
);

export const selectCallback = createSelector(
  selectAuthState,
  state => state.callback,
);

export const selectIsAPIAuthorized = createSelector(
  selectAuthState,
  state => state.isAPIAuthorized,
);

export const selectIsMarketingAccepted = createSelector(
  selectAuthState,
  state => {
    return state.isMarketingAccepted ?? getIsMarketingAccepted();
  },
);

export const selectIsSignedIn = createSelector(
  selectAuthState,
  state => state.isSignedIn,
);

export const selectIsSignedOutManually = createSelector(
  selectAuthState,
  state => state.isSignedOutManually,
);

export const selectIsAPIAuthorizing = createSelector(
  selectIsSignedIn,
  selectIsAPIAuthorized,
  (isSignedIn, isAPIAuthorized) => isSignedIn && !isAPIAuthorized,
);

export const selectIsLoggingIn = selectIsAPIAuthorizing;

export const selectReferralCode = createSelector(
  selectAuthState,
  state => state.referralCode ?? getAuthReferralCode(),
);

export const selectWalletId = createSelector(
  selectAuthState,
  state => state.walletId ?? getAuthWalletId(),
);

export const selectWalletIcon = createSelector(
  selectWalletId,
  selectIsLightTheme,
  (walletId, isLightTheme) =>
    walletId
      ? getWalletIcon(walletId)
      : getChainIcon(ChainID.ETH, isLightTheme),
);

export const selectWalletName = createSelector(selectWalletId, walletId =>
  walletId ? getWalletName(walletId) : undefined,
);

export const selectIsLoggedIn = createSelector(
  selectIsSignedIn,
  selectIsAPIAuthorized,
  (isSignedIn, isAPIAuthorized) => isSignedIn && isAPIAuthorized,
);

export const selectIsGithubAuth = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Github,
);

export const selectIsGoogleAuth = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Google,
);

export const selectIsOAuth = createSelector(
  selectIsGithubAuth,
  selectIsGoogleAuth,
  (isGithubAuth, isGoogleAuth) => isGithubAuth || isGoogleAuth,
);

export const selectIsWeb3Auth = createSelector(
  selectAuthProvider,
  authProvider => authProvider === AuthProvider.Web3,
);
